import {ArrowDownIcon, ClockIcon, MapPinIcon, TruckIcon} from "@heroicons/react/20/solid";
import {getLocationMetaActivity, type LocationContext, type LocationMetaRelationship, type LocationMetaWork} from "@ots-frontend/ots-data";
import type {ReactNode} from "react";
import {OtsBadge} from "../../components/OtsBadge";
import {DescriptionItem} from "../../components/DescriptionList";
import {OtsDateTime} from "../../components/OtsDateTime";
import {formatAgo} from "../site-containers";
import {SiteContainerFillBar} from "../SiteContainerFillBar";
import {MapInfoFields, MapInfoPanel, MapInfoSection} from "./MapInfoPanel";
import {formatCoordinates, formatMapNumber, formatMapQuantity, plainMapEntity, type RenderMapEntity} from "./map-panel";

export type UnitInfoPanelViewProps = {
  ctx: LocationContext;
  now: number;
  renderEntity?: RenderMapEntity;
  onClose?: () => void;
};

const RELATIONSHIP_LABELS: Record<string, string> = {
  DRIVER: "Driver",
  DRIVES: "Drives",
  HAULED_BY: "Hauled by",
  HAULING: "Hauling",
  HAULS: "Hauls",
};

/** Above this share of capacity a trailer's load bar turns amber: it is nearly full. */
export const TRAILER_NEARLY_FULL_PERCENT = 85;

/** The trailer's load: its own attributes when the unit is a trailer, otherwise the trailer it is hauling. */
export const unitTrailerLoad = (ctx: LocationContext) => {
  const attributes = ctx.entityType === "TRAILER"
    ? ctx.entityAttributes
    : ctx.meta?.relationships?.find(rel => rel.entity.entityType === "TRAILER")?.entity?.entityAttributes;
  const capacity = attributes?.capacity as number | undefined;
  const onboard = attributes?.onboardQuantity as number | undefined;
  // An empty trailer (0 onboard) has no bar, rather than a stray 0.
  if (!capacity || !onboard) return undefined;
  return {onboard, capacity, percent: Math.round((onboard / capacity) * 100)};
};

const Stop = ({icon, site, company, at, quantity, freight, renderEntity, siteId}: {
  icon: ReactNode; site?: string; siteId?: string; company?: string; at?: number;
  quantity?: string; freight?: string; renderEntity: RenderMapEntity;
}) => (
  <div className="flex gap-2">
    <span aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-[var(--ots-primary)] [&>svg]:size-full">{icon}</span>
    <div className="flex min-w-0 flex-col gap-0.5 text-xs">
      <span className="font-semibold text-[var(--ots-text)]">
        {siteId ? renderEntity({id: siteId, name: site, type: "SITE"}) : site}
        {company && <span className="font-normal text-[var(--ots-text-muted)]"> · {company}</span>}
      </span>
      {at != null && <span className="text-[var(--ots-text-muted)]"><OtsDateTime value={at}/></span>}
      {(quantity || freight) && (
        <span className="text-[var(--ots-text)]">{[quantity, freight].filter(Boolean).join(" · ")}</span>
      )}
    </div>
  </div>
);

const WorkStops = ({work, renderEntity}: {work: LocationMetaWork; renderEntity: RenderMapEntity}) => {
  const delivered = Boolean(work.deliverySiteName || work.deliverySiteCompanyName || work.deliveryInDttm || work.deliveryOutDttm || work.deliveryQuantity);
  return <div className="flex flex-col gap-1">
    <Stop
      icon={<MapPinIcon/>}
      site={work.pickupSiteName || "Pickup"}
      siteId={work.pickupSiteId}
      company={work.pickupSiteCompanyName}
      at={work.pickupDttm}
      quantity={formatMapQuantity(work.pickupQuantity, work.pickupQuantityUom)}
      freight={work.pickupFreightTypeName}
      renderEntity={renderEntity}/>
    <span aria-hidden="true" className="ml-0.5 size-3 text-[var(--ots-text-muted)] [&>svg]:size-full"><ArrowDownIcon/></span>
    {delivered
      ? <Stop
        icon={<TruckIcon/>}
        site={work.deliverySiteName || "Delivery"}
        siteId={work.deliverySiteId}
        company={work.deliverySiteCompanyName}
        at={work.deliveryDttm ?? work.deliveryInDttm}
        quantity={formatMapQuantity(work.deliveryQuantity, work.deliveryQuantityUom)}
        freight={work.deliveryFreightTypeName}
        renderEntity={renderEntity}/>
      : <div className="flex items-center gap-2 text-xs text-[var(--ots-text-muted)]">
        <span aria-hidden="true" className="size-4 [&>svg]:size-full"><ClockIcon/></span>
        Delivery pending
      </div>}
  </div>;
};

const Relationship = ({rel, renderEntity}: {rel: LocationMetaRelationship; renderEntity: RenderMapEntity}) => (
  <li className="text-xs">
    <span className="text-[var(--ots-text-muted)]">{RELATIONSHIP_LABELS[rel.type] ?? rel.type} · </span>
    <span className="font-semibold text-[var(--ots-text)]">
      {renderEntity({id: rel.entity.entityId, name: rel.entity.entityName, type: rel.entity.entityType})}
    </span>
  </li>
);

/** The live map's panel for a clicked driver, trailer or vehicle: where it is, who is with it, and its work. */
export const UnitInfoPanelView = ({ctx, now, renderEntity = plainMapEntity, onClose}: UnitInfoPanelViewProps) => {
  const activity = getLocationMetaActivity(ctx.meta);
  const work = ctx.meta?.work;
  const load = unitTrailerLoad(ctx);
  const relationships = ctx.meta?.relationships ?? [];
  const destinations = Object.entries(ctx.meta?.possibleDestinations ?? {});
  const location = ctx.location;

  return <MapInfoPanel
    ariaLabel={`${ctx.entityName ?? "Unit"} location`}
    icon={<TruckIcon/>}
    title={ctx.entityName}
    subtitle={ctx.companyId ? ctx.companyName : undefined}
    badges={<>
      <OtsBadge size="compact" tone={activity?.inShift ? "success" : "danger"} dot>{activity?.inShift ? "In shift" : "Out of shift"}</OtsBadge>
      {work?.status === "IN_PROGRESS" && <OtsBadge size="compact" tone="success">Work in progress</OtsBadge>}
      {activity?.onSite && <OtsBadge size="compact" tone="info">On site</OtsBadge>}
      {activity?.status && <OtsBadge size="compact">{activity.status}</OtsBadge>}
    </>}
    onClose={onClose}>
    {ctx.message && <p className="!m-0 rounded-md bg-[var(--ots-surface-variant)] px-3 py-2 text-xs text-[var(--ots-text)]">{ctx.message}</p>}
    {location && (
      <MapInfoSection title="Location">
        <MapInfoFields>
          <DescriptionItem label="GPS">{formatCoordinates(location.latitude, location.longitude)}</DescriptionItem>
          <DescriptionItem label="Speed">{location.speed ? formatMapQuantity(location.speed, "mph") : undefined}</DescriptionItem>
          <DescriptionItem label="Last ping">
            <span title={new Date(location.dttm).toLocaleString()}>{formatAgo(location.dttm, now)}</span>
          </DescriptionItem>
        </MapInfoFields>
      </MapInfoSection>
    )}
    {relationships.length > 0 && (
      <MapInfoSection title="With">
        <ul className="!m-0 flex list-none flex-col gap-1 !p-0">
          {relationships.map((rel, index) => <Relationship key={index} rel={rel} renderEntity={renderEntity}/>)}
        </ul>
      </MapInfoSection>
    )}
    {work && (
      <MapInfoSection
        title="Work"
        aside={work.status === "IN_PROGRESS" && <OtsBadge size="compact" tone="success">In progress</OtsBadge>}>
        <WorkStops work={work} renderEntity={renderEntity}/>
        {load && (
          <div className="flex flex-col gap-1">
            <SiteContainerFillBar
              label="Trailer load"
              percent={load.percent}
              tone={load.percent > TRAILER_NEARLY_FULL_PERCENT ? "warning" : "normal"}/>
            <span className="text-[11px] tabular-nums text-[var(--ots-text-muted)]">
              {formatMapNumber(load.onboard, 0)} / {formatMapNumber(load.capacity, 0)} onboard
            </span>
          </div>
        )}
      </MapInfoSection>
    )}
    {destinations.length > 0 && (
      <MapInfoSection title="Possible destinations">
        <ul className="!m-0 flex list-none flex-col gap-3 !p-0">
          {destinations.map(([source, destination]) => (
            <li key={source} className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-[var(--ots-text)]">
                {destination.siteId ? renderEntity({id: destination.siteId, name: destination.siteName, type: "SITE"}) : "(unknown)"}
                <span className="font-normal text-[var(--ots-text-muted)]"> · from {source}</span>
              </span>
              <MapInfoFields>
                <DescriptionItem label="Distance">{destination.distance ? formatMapQuantity(destination.distance, "mi") : undefined}</DescriptionItem>
                <DescriptionItem label="ETA"><OtsDateTime value={destination.etaDttm}/></DescriptionItem>
              </MapInfoFields>
            </li>
          ))}
        </ul>
      </MapInfoSection>
    )}
  </MapInfoPanel>;
};
